import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, CheckCircle, AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const ReschedulePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams?.get('token');

  const [step, setStep] = useState('loading'); // loading | valid | form | success | error
  const [appointment, setAppointment] = useState(null);
  const [clientName, setClientName] = useState('');
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
  ];

  useEffect(() => {
    if (!token) {
      setStep('error');
      setErrorMsg('Lien de reprogrammation invalide ou manquant.');
      return;
    }
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const { data, error } = await supabase?.functions?.invoke('handle-reschedule', {
        body: { token },
      });

      if (error || !data?.valid) {
        setStep('error');
        setErrorMsg(
          data?.error === 'Token already used' ?'Ce lien a déjà été utilisé. Veuillez contacter notre équipe pour reprogrammer.'
            : data?.error === 'Token expired' ?'Ce lien de reprogrammation a expiré. Veuillez contacter notre équipe.' :'Lien invalide ou expiré.'
        );
        return;
      }

      setAppointment(data?.appointment);
      setClientName(data?.client_name || '');
      setStep('form');
    } catch (err) {
      setStep('error');
      setErrorMsg('Impossible de valider le lien. Veuillez réessayer.');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!newDate) return;

    setSubmitting(true);
    setErrorMsg('');

    try {
      const { data, error } = await supabase?.functions?.invoke('handle-reschedule', {
        body: { token, new_date: newDate, new_time: newTime || undefined },
      });

      if (error || !data?.success) {
        setErrorMsg(data?.error || 'Erreur lors de la reprogrammation.');
        return;
      }

      setStep('success');
    } catch (err) {
      setErrorMsg('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  const getMinDate = () => new Date()?.toISOString()?.split('T')?.[0];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr)?.toLocaleDateString('fr-FR', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-brand-gold rounded-xl flex items-center justify-center">
              <span className="text-black font-bold text-sm">KS</span>
            </div>
            <span className="text-xl font-semibold text-text-primary">Kapital States</span>
          </div>
        </div>

        {/* Loading */}
        {step === 'loading' && (
          <div className="bg-surface rounded-2xl p-8 text-center border border-border">
            <div className="w-12 h-12 border-2 border-brand-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-text-secondary">Validation du lien en cours…</p>
          </div>
        )}

        {/* Error */}
        {step === 'error' && (
          <div className="bg-surface rounded-2xl p-8 text-center border border-border">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-text-primary mb-2">Lien invalide</h2>
            <p className="text-text-secondary mb-6">{errorMsg}</p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-brand-gold hover:underline text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour à l'accueil
            </button>
          </div>
        )}

        {/* Form */}
        {step === 'form' && (
          <div className="bg-surface rounded-2xl border border-border overflow-hidden">
            <div className="bg-gradient-to-r from-brand-gold/10 to-brand-gold/5 border-b border-border p-6">
              <div className="flex items-center gap-3 mb-1">
                <RefreshCw className="w-5 h-5 text-brand-gold" />
                <h1 className="text-xl font-semibold text-text-primary">Reprogrammer le rendez-vous</h1>
              </div>
              {clientName && (
                <p className="text-text-secondary text-sm">Bonjour <strong className="text-text-primary">{clientName}</strong>, choisissez une nouvelle date.</p>
              )}
            </div>

            {/* Current appointment info */}
            {appointment && (
              <div className="px-6 pt-5 pb-0">
                <div className="bg-background rounded-xl p-4 border border-border mb-5">
                  <p className="text-xs text-text-tertiary uppercase tracking-wide font-medium mb-2">Rendez-vous actuel</p>
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center gap-2 text-sm text-text-secondary">
                      <Calendar className="w-4 h-4 text-brand-gold" />
                      <span>{formatDate(appointment?.scheduled_date)}</span>
                    </div>
                    {appointment?.scheduled_time && (
                      <div className="flex items-center gap-2 text-sm text-text-secondary">
                        <Clock className="w-4 h-4 text-brand-gold" />
                        <span>{appointment?.scheduled_time}</span>
                      </div>
                    )}
                  </div>
                  {appointment?.appointment_type && (
                    <p className="text-sm text-brand-gold font-medium mt-2">{appointment?.appointment_type}</p>
                  )}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-5">
              {errorMsg && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {errorMsg}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Nouvelle date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e?.target?.value)}
                  min={getMinDate()}
                  required
                  className="w-full px-4 py-3 border border-border rounded-xl bg-background text-text-primary focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Nouvel horaire (optionnel)
                </label>
                <select
                  value={newTime}
                  onChange={(e) => setNewTime(e?.target?.value)}
                  className="w-full px-4 py-3 border border-border rounded-xl bg-background text-text-primary focus:ring-2 focus:ring-brand-gold focus:border-transparent"
                >
                  <option value="">Sélectionner un horaire</option>
                  {timeSlots?.map((slot) => (
                    <option key={slot} value={slot}>{slot}</option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                disabled={!newDate || submitting}
                className="w-full py-3 px-6 bg-brand-gold text-black font-semibold rounded-xl hover:bg-brand-gold/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    Reprogrammation…
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Confirmer la reprogrammation
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* Success */}
        {step === 'success' && (
          <div className="bg-surface rounded-2xl p-8 text-center border border-border">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-text-primary mb-2">Rendez-vous reprogrammé !</h2>
            <p className="text-text-secondary mb-2">
              Votre rendez-vous a été reprogrammé au{' '}
              <strong className="text-text-primary">{formatDate(newDate)}</strong>
              {newTime && <> à <strong className="text-text-primary">{newTime}</strong></>}.
            </p>
            <p className="text-text-tertiary text-sm mb-6">
              Notre équipe vous contactera pour confirmer les nouveaux détails.
            </p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-brand-gold hover:underline text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour à l'accueil
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReschedulePage;
